import type { DeleteTaxonomyOrgMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * Input for the compound taxonomy_org orphan-cleanup delete (RSP-6488). Only
 * the `TaxonomyId` is supplied; `OrgKey` is derived server-side from the caller
 * (the legacy GraphQL took `OrgKey` explicitly — the tRPC path never trusts a
 * client-sent org).
 */
export interface DeleteTaxonomyOrgInput {
  TaxonomyId: string;
}

/**
 * Reshapes the single-count tRPC response to the two-mutation GraphQL shape.
 * The data-layer performs both deletes in one transaction and returns the
 * `taxonomy_org` affected count; the parent-taxonomy count is not surfaced
 * separately, so it is reported as the same affected-row signal.
 */
const mapTrpcResponseToGraphQL = (data: {
  affected_rows: number;
}): DeleteTaxonomyOrgMutation => ({
  delete_taxonomy_org: {
    __typename: 'taxonomy_org_mutation_response',
    affected_rows: data.affected_rows,
  },
  delete_taxonomy: {
    __typename: 'taxonomy_mutation_response',
    affected_rows: data.affected_rows,
  },
});

export const useDeleteTaxonomyOrgTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.taxonomy.deleteOrg.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.taxonomy.taxonomyAudit.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.taxonomy.taxonomyByLocale.queryKey(),
        });
      },
    }),
  });

  return {
    deleteTaxonomyOrg: async (
      input: DeleteTaxonomyOrgInput
    ): Promise<DeleteTaxonomyOrgMutation> => {
      const result = await mutation.mutateAsync({
        TaxonomyId: input.TaxonomyId,
      });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
